import {
  NAV_LABEL_OPTIONS,
  ROUTES,
  PERMISSIONS,
} from 'src/constants';
import {
  Folder as FolderIcon,
  Users as UsersIcon
} from 'react-feather';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { ReactComponent as EventSettingsIcon } from 'src/assets/images/events-settings.svg';
import { ReactComponent as OrderFollowUpSettingsIcon } from 'src/assets/images/order-follow-up-settings.svg';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { ReactComponent as MessagingSettings } from 'src/assets/images/messaging-settings.svg';
import { ReactComponent as GroupSettingsIcon } from 'src/assets/images/groups-settings.svg';
import TuneIcon from '@mui/icons-material/Tune';
import LinkIcon from '@mui/icons-material/Link';
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { ACTIVITY_AREA } from 'src/actions/activityActions';

const navConfig = (logout) => ([
  {
    subheader: 'View & Manage',
    permissions: [
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.EVENT_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.CONTACT_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.CHAT_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.GROUP_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_FORMS_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_LINKS_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SHARE_SETS_SIDEBAR,
    ],
    items: [
      {
        title: 'Order Follow-Up',
        icon: FolderIcon,
        href: '/app/management/followUp',
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_SIDEBAR],
        additionalOptions: { badgeKey: ACTIVITY_AREA.ORDERS },
      },
      {
        title: 'Events',
        icon: EventAvailableOutlinedIcon,
        href: '/app/management/events',
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.EVENT_SIDEBAR],
        additionalOptions: { badgeKey: ACTIVITY_AREA.PARTY_GUESTS },
      },
      {
        title: 'Contacts',
        icon: UsersIcon,
        href: '/app/management/customers',
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.CONTACT_SIDEBAR],
      },
      {
        title: 'Connections',
        icon: QuestionAnswerOutlinedIcon,
        href: ROUTES.APP_MANAGEMENT_CHAT,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.CHAT_SIDEBAR],
        additionalOptions: {
          badgeKey: 'chat',
        },
      },
      {
        title: 'Scheduled Connections',
        icon: ScheduleSendOutlinedIcon,
        href: ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_SIDEBAR],
      },
      {
        title: 'Groups',
        icon: GroupsOutlinedIcon,
        href: ROUTES.APP_MANAGEMENT_GROUPS,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.GROUP_SIDEBAR],
        additionalOptions: { badgeKey: ACTIVITY_AREA.GROUP_MEMBERS },
      },
      {
        // Non-breaking space keeps "Connect Pages" together so it wraps as a
        // unit onto the second line, rather than "Pages" dangling alone.
        title: 'Smart Forms & Connect Pages',
        icon: DynamicFormOutlinedIcon,
        href: ROUTES.APP_SMART_FORMS,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_FORMS_SIDEBAR],
        // CON-XXXX — new-entries badge; shown only with ACTIVITY_BADGES.
        additionalOptions: { badgeKey: ACTIVITY_AREA.SMART_FORM_ENTRIES },
      },
      {
        // Nav placement only (CON-1549) — the route stays under
        // /app/settings/smart-links; bookmarks and help links depend on it.
        title: 'Smart Links',
        icon: LinkIcon,
        href: ROUTES.APP_SMART_LINKS_SETTINGS,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_LINKS_SIDEBAR],
      },
      {
        // CON-1552 — Share Center (universal share-code entry + Share Sets)
        title: 'Share Center',
        icon: ShareOutlinedIcon,
        href: ROUTES.APP_SHARE_CENTER,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SHARE_SETS_SIDEBAR],
      },
    ]
  },
  {
    subheader: 'Settings',
    href: '/app/pages',
    permissions: [
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_SETTINGS_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.MESSAGE_TEMPLATES_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.GROUP_SETTINGS_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_FIELDS_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.VALUE_LISTS_SIDEBAR,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_SIDEBAR,
    ],
    items: [
      {
        title: 'Order Follow-Up Settings',
        href: '/app/order-settings',
        icon: OrderFollowUpSettingsIcon,
        isShownWhenOtherHidden: true,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_SETTINGS_SIDEBAR],
      },
      {
        title: 'Events Settings',
        href: '/app/event-settings',
        icon: EventSettingsIcon,
        isShownWhenOtherHidden: true,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_SIDEBAR],
      },
      {
        title: 'Contact Settings',
        href: ROUTES.APP_CONTACT_SETTINGS,
        icon: ManageAccountsIcon,
        isShownWhenOtherHidden: true,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.CONTACT_SETTINGS_SIDEBAR],
      },
      {
        title: 'Messaging Settings',
        href: ROUTES.APP_CHAT_SETTINGS,
        icon: MessagingSettings,
        isShownWhenOtherHidden: true,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.MESSAGE_SETTINGS_SIDEBAR],
      },
      {
        title: 'Message Templates',
        href: '/app/settings/messages',
        icon: MessageOutlinedIcon,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.MESSAGE_TEMPLATES_SIDEBAR],
      },
      {
        title: 'Groups Settings',
        href: ROUTES.APP_GROUP_SETTINGS,
        icon: GroupSettingsIcon,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.GROUP_SETTINGS_SIDEBAR],
      },
      {
        title: 'Smart Fields',
        href: ROUTES.APP_SMART_FIELDS_SETTINGS,
        icon: TuneIcon,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_FIELDS_SIDEBAR],
      },
      {
        // CON-1597 — Value Lists (reusable dropdown option sets)
        title: 'Lists',
        href: ROUTES.APP_VALUE_LISTS,
        icon: ListAltOutlinedIcon,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.VALUE_LISTS_SIDEBAR],
      },
      {
        title: 'Account & General Settings',
        href: '/app/account',
        icon: SettingsIcon,
        isShownWhenOtherHidden: true,
        permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_SIDEBAR],
      },
    ]
  },
  // Admin section.
  //
  // Section visibility uses the OR (`permissions`) gate only — the section
  // shows for any user holding at least one item-level admin permission.
  // Per-item visibility is gated by each item's own `requiredPermissions`,
  // so non-admin-tooling users granted a single admin permission (e.g.,
  // CONTACTS_BULK_MERGE_ADMIN) see only the items they can use. Originally
  // the section had `requiredPermissions: [ADMIN_TOOLS_VIEW]` (CON-1487
  // Phase G) which conflicted with that intent and was removed.
  {
    subheader: 'Admin',
    icon: AdminPanelSettingsIcon,
    permissions: [
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.EVENT_TEMPLATE_DEFAULTS_ADMIN,
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.CONTACTS_BULK_MERGE_ADMIN,
      // CON-1532 Phase 9 — surfaces the Admin section for users with the
      // new admin permission too.
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.PAGE_COLUMN_DEFAULTS_ADMIN,
      // CON-1488 — surfaces the Admin section for users with the
      // TCR Registrations admin permission.
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.TCR_REGISTRATIONS_ADMIN,
    ],
    items: [
      {
        title: 'Event App Templates',
        icon: DescriptionOutlinedIcon,
        href: '/app/event-templates/admin',
        requiredPermissions: [
          PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ADMIN_TOOLS_VIEW,
          PERMISSIONS.PERMISSIONS_SYSTEM_CODE.EVENT_TEMPLATE_DEFAULTS_ADMIN,
        ],
      },
      {
        title: 'Bulk Contact Merge',
        icon: MergeTypeIcon,
        href: '/app/admin/bulk-contact-merge',
        requiredPermissions: [
          PERMISSIONS.PERMISSIONS_SYSTEM_CODE.CONTACTS_BULK_MERGE_ADMIN,
        ],
      },
      // CON-1532 Phase 9 — Page Column Defaults admin page (company-tier
      // defaults for the three V2 pageKeys).
      {
        title: 'Page Column Defaults',
        icon: ViewColumnIcon,
        href: ROUTES.APP_ADMIN_PAGE_COLUMN_DEFAULTS,
        requiredPermissions: [
          PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ADMIN_TOOLS_VIEW,
          PERMISSIONS.PERMISSIONS_SYSTEM_CODE.PAGE_COLUMN_DEFAULTS_ADMIN,
        ],
      },
      // CON-1488 — TCR Registrations admin list page.
      {
        title: 'TCR Registrations',
        icon: ListAltIcon,
        href: ROUTES.APP_ADMIN_TCR_REGISTRATIONS,
        requiredPermissions: [
          PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ADMIN_TOOLS_VIEW,
          PERMISSIONS.PERMISSIONS_SYSTEM_CODE.TCR_REGISTRATIONS_ADMIN,
        ],
      },
    ],
  },
  // CON-1699 — Setup Assistant. Two gates, both must hold: `permissions`
  // (SETUP_ASSISTANT, the same code the route guard and every API route
  // check — there is no separate *_SIDEBAR code) and `cpTextingOnly`,
  // filtered in NavBar/index.js on textingProvider === 'CP', the same value
  // the loader gates on. isShownWhenOtherHidden: a brand-new account stuck
  // on required settings is exactly who this page is for.
  // additionalOptions.badgeKey 'setupAssistant': NavItem wraps the icon in
  // SetupAssistantBadge (outstanding verifiable steps).
  {
    subheader: 'Setup Assistant',
    isLink: true,
    href: ROUTES.APP_SETUP_ASSISTANT,
    icon: ChecklistIcon,
    isShownWhenOtherHidden: true,
    cpTextingOnly: true,
    permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SETUP_ASSISTANT],
    additionalOptions: {
      badgeKey: 'setupAssistant',
    },
    items: []
  },
  {
    subheader: 'Help & Information',
    isLink: true,
    href: ROUTES.APP_HELP_AND_INFORMATION,
    icon: HelpOutlineIcon,
    isShownWhenOtherHidden: true,
    permissions: [PERMISSIONS.PERMISSIONS_SYSTEM_CODE.HELP_INFORMATION_SIDEBAR],
    items: []
  },
  {
    subheader: NAV_LABEL_OPTIONS.LOGOUT,
    isLink: true,
    href: '/',
    click: logout,
    icon: ExitToAppIcon,
    isShownWhenOtherHidden: true,
    items: []
  },
]);

export default navConfig;
